// BEGIN FILE: src/utils/futureEventPredictor.ts
/**
 * Future Event Prediction Engine
 * 
 * Computes opportunity and caution windows for the next 6-12 months
 * (2 years for marriage) using existing feeds rules logic.
 */

import { DateTime } from 'luxon';
import type { DashaContext, TransitContext, FeedItem, LifeArea, DashaLevel, HouseNum } from '../astro/feeds/types';
import { RULES, NATAL_PREDICTION_RULES } from '../astro/feeds/rules';
import { TEMPLATES, type TemplateKey } from '../astro/feeds/templates';
import { computeMahadashaTimelineAround, getCurrentDashaLevels } from './vimshottari';
import type { PlanetName } from './astroEngine';
import { classifyFunctionalForLagna } from './astroEngine';

// Simple cache to avoid recomputation
let lastComputeParams: string | null = null;
let lastComputeResult: { opportunities: FutureEvent[]; cautions: FutureEvent[] } | null = null;

export type FutureEventCategory =
  // Opportunities
  | 'career_success' | 'wealth_inflow' | 'business_success' | 'investments_opportunity'
  | 'property_gains' | 'marriage_relationship' | 'education' | 'spiritual_growth'
  | 'travel_opportunity' | 'children_blessing' | 'legal_resolution' | 'recognition_awards'
  // Cautions
  | 'health_hazards' | 'accident_risk' | 'monetary_loss' | 'job_safeguard'
  | 'excessive_expenses' | 'no_investment' | 'litigation_risk' | 'relationship_strain'
  | 'mother_health' | 'father_health' | 'children_safety' | 'travel_hazards'
  | 'mental_stress' | 'property_disputes' | 'chronic_health' | 'hidden_enemies'
  | 'debt_caution' | 'reputation_risk' | 'foreign_loss' | 'addiction_risk'
  | 'spiritual_crisis';

export interface FutureEvent {
  id: string;
  category: FutureEventCategory;
  type: 'opportunity' | 'caution';
  severity: 'positive' | 'mixed' | 'caution' | 'alert';
  score: number; // -100 to +100
  title: string;
  description: string;
  window: { from: string; to: string };
  confidence: number; // 0-100 (derived from abs(score))
  lifeAreas: LifeArea[];
  remedies?: string;
  evidence: string[];
  highProbability?: boolean; // true if Rahu is in any dasha level (MD/AD/PD/SD)
  
  // Consolidation fields
  isConsolidated?: boolean; // true if this represents multiple merged events
  rahuPeakWindows?: Array<{ from: string; to: string }>; // High-probability sub-windows during Rahu periods
  consolidatedCount?: number; // Number of events merged into this one
}

/**
 * Map life areas to future event categories
 */
const LIFE_AREA_TO_CATEGORIES: Record<LifeArea, FutureEventCategory[]> = {
  Career: ['career_success', 'job_safeguard', 'recognition_awards'],
  Wealth: ['wealth_inflow', 'monetary_loss', 'excessive_expenses', 'debt_caution'],
  Investments: ['investments_opportunity', 'no_investment'],
  Property: ['property_gains', 'property_disputes'],
  Spouse: ['marriage_relationship', 'relationship_strain'],
  Children: ['children_blessing', 'children_safety'],
  Health: ['health_hazards', 'chronic_health', 'mental_stress'],
  AccidentRisk: ['accident_risk'],
  Mother: ['mother_health'],
  Father: ['father_health'],
  Travel: ['travel_opportunity', 'travel_hazards', 'foreign_loss'],
  Litigation: ['legal_resolution', 'litigation_risk', 'hidden_enemies'],
  Spirituality: ['spiritual_growth', 'spiritual_crisis'],
  Self: ['recognition_awards', 'reputation_risk', 'addiction_risk']
};

/**
 * Map feed template keys to future event categories
 */
const TEMPLATE_TO_CATEGORY: Partial<Record<string, FutureEventCategory>> = {
  promotion_opportunity_positive: 'career_success',
  promotion_opportunity_alert: 'job_safeguard',
  career_visibility: 'career_success',
  wealth_gains_positive: 'wealth_inflow',
  wealth_gains_alert: 'monetary_loss',
  investment_caution_positive: 'investments_opportunity',
  investment_caution_alert: 'no_investment',
  property_gains_positive: 'property_gains',
  property_gains_alert: 'property_disputes',
  marriage_timing: 'marriage_relationship',
  marriage_timing_positive: 'marriage_relationship',
  marriage_timing_alert: 'relationship_strain',
  spouse_boundaries: 'relationship_strain',
  spouse_boundaries_positive: 'marriage_relationship',
  children_blessing_positive: 'children_blessing',
  children_blessing_alert: 'children_safety',
  health_hygiene_alert: 'health_hazards',
  health_hygiene_positive: 'health_hazards', // still health-related
  accident_risk: 'accident_risk',
  accident_risk_alert: 'accident_risk',
  mother_health_alert: 'mother_health',
  mother_health_positive: 'mother_health',
  father_guidance: 'father_health',
  father_guidance_positive: 'father_health',
  travel_gains_positive: 'travel_opportunity',
  travel_gains_alert: 'travel_hazards',
  litigation_caution_alert: 'litigation_risk',
  litigation_caution_positive: 'legal_resolution',
  spiritual_growth_positive: 'spiritual_growth',
  spiritual_growth_alert: 'spiritual_crisis',
  self_development_alert: 'reputation_risk',
};

/**
 * Get category display info
 */
export function getCategoryInfo(category: FutureEventCategory): {
  label: string;
  icon: string;
  color: string;
  description: string;
} {
  const info: Record<FutureEventCategory, any> = {
    // Opportunities
    career_success: { label: 'Career Success', icon: '🚀', color: 'success', description: 'Promotions, recognition, job offers' },
    wealth_inflow: { label: 'Wealth Inflow', icon: '💰', color: 'success', description: 'Income gains, bonuses, windfalls' },
    business_success: { label: 'Business Success', icon: '📈', color: 'success', description: 'Ventures, partnerships, expansion' },
    investments_opportunity: { label: 'Investment Opportunity', icon: '📊', color: 'success', description: 'Favorable investment timing' },
    property_gains: { label: 'Property Gains', icon: '🏠', color: 'success', description: 'Real estate, home purchase' },
    marriage_relationship: { label: 'Marriage/Relationship', icon: '💑', color: 'success', description: 'Commitment, harmony, partnership' },
    education: { label: 'Education', icon: '📚', color: 'success', description: 'Learning, certifications, studies' },
    spiritual_growth: { label: 'Spiritual Growth', icon: '🕉️', color: 'success', description: 'Meditation, pilgrimage, wisdom' },
    travel_opportunity: { label: 'Travel Opportunity', icon: '✈️', color: 'success', description: 'Beneficial journeys, relocation' },
    children_blessing: { label: 'Children Blessing', icon: '👶', color: 'success', description: 'Conception, education success' },
    legal_resolution: { label: 'Legal Resolution', icon: '⚖️', color: 'success', description: 'Court outcomes, settlements' },
    recognition_awards: { label: 'Recognition/Awards', icon: '🏆', color: 'success', description: 'Public honors, fame' },
    
    // Cautions
    health_hazards: { label: 'Health Hazards', icon: '🏥', color: 'danger', description: 'Chronic conditions, medical attention needed' },
    accident_risk: { label: 'Accident Risk', icon: '⚠️', color: 'danger', description: 'Physical safety, vehicle caution' },
    monetary_loss: { label: 'Monetary Loss', icon: '💸', color: 'danger', description: 'Financial drainage, fraud risk' },
    job_safeguard: { label: 'Job Safeguard', icon: '🛡️', color: 'warning', description: 'Career instability, office politics' },
    excessive_expenses: { label: 'Excessive Expenses', icon: '🔴', color: 'warning', description: 'Unwanted expenditures' },
    no_investment: { label: 'No Investment Zone', icon: '🚫', color: 'danger', description: 'Avoid aggressive positions' },
    litigation_risk: { label: 'Litigation Risk', icon: '⚖️', color: 'danger', description: 'Legal disputes, confrontations' },
    relationship_strain: { label: 'Relationship Strain', icon: '💔', color: 'warning', description: 'Partnership conflicts' },
    mother_health: { label: 'Mother Health', icon: '👩', color: 'warning', description: 'Parent health vigilance' },
    father_health: { label: 'Father Health', icon: '👨', color: 'warning', description: 'Parent health monitoring' },
    children_safety: { label: 'Children Safety', icon: '🧒', color: 'warning', description: 'Health/education challenges' },
    travel_hazards: { label: 'Travel Hazards', icon: '🚧', color: 'warning', description: 'Delays, complications' },
    mental_stress: { label: 'Mental Stress', icon: '😰', color: 'warning', description: 'Anxiety, burnout risk' },
    property_disputes: { label: 'Property Disputes', icon: '🏚️', color: 'warning', description: 'Real estate conflicts' },
    chronic_health: { label: 'Chronic Health', icon: '💊', color: 'warning', description: 'Long-term conditions' },
    hidden_enemies: { label: 'Hidden Enemies', icon: '🎭', color: 'danger', description: 'Betrayal, sabotage risk' },
    debt_caution: { label: 'Debt Caution', icon: '📉', color: 'warning', description: 'Loan burden, credit issues' },
    reputation_risk: { label: 'Reputation Risk', icon: '👁️', color: 'danger', description: 'Public image threats' },
    foreign_loss: { label: 'Foreign Loss', icon: '🌍', color: 'warning', description: 'International deals failing' },
    addiction_risk: { label: 'Addiction Risk', icon: '🍷', color: 'danger', description: 'Substance abuse tendencies' },
    spiritual_crisis: { label: 'Spiritual Crisis', icon: '🌑', color: 'warning', description: 'Faith challenges' },
  };
  
  return info[category] || { label: category, icon: '📍', color: 'neutral', description: '' };
}

/**
 * Get sign name from 1-based index
 */
function getSignName(signIndex: number): string {
  const signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 
                 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
  return signs[signIndex - 1] || `Sign ${signIndex}`;
}

/**
 * Get house name with traditional signification
 */
function getHouseName(houseNum: number): string {
  const houses: Record<number, string> = {
    1: '1st house (Self, Personality)',
    2: '2nd house (Wealth, Family)',
    3: '3rd house (Courage, Siblings)',
    4: '4th house (Mother, Property)',
    5: '5th house (Children, Creativity)',
    6: '6th house (Health, Enemies)',
    7: '7th house (Spouse, Partnership)',
    8: '8th house (Longevity, Occult)',
    9: '9th house (Fortune, Father)',
    10: '10th house (Career, Status)',
    11: '11th house (Gains, Friends)',
    12: '12th house (Loss, Liberation)'
  };
  return houses[houseNum] || `${houseNum}th house`;
}

/**
 * Compute future dasha periods for scanning
 * Returns periods at PD/SD level where actual events occur
 * MD/AD provide broader themes, PD/SD + transits trigger specific events
 */
function getFutureDashaPeriods(
  birthISO: string,
  moonLongDeg: number,
  scanMonths: number = 12
): Array<{ from: string; to: string; level: 'PD' | 'SD'; planet: string }> {
  const now = DateTime.now();
  const startScan = now.plus({ months: 3 }); // Start 3 months ahead
  const endScan = now.plus({ months: scanMonths });
  
  // We'll sample at key points where PD or SD changes
  // OPTIMIZATION: Check every 2 weeks instead of weekly to reduce computation
  const periods: Array<{ from: string; to: string; level: 'PD' | 'SD'; planet: string }> = [];
  
  let currentDate = startScan;
  let lastPDPlanet: string | null = null;
  let lastSDPlanet: string | null = null;
  
  // Sample every 2 weeks to catch major PD transitions (SD too granular)
  while (currentDate < endScan) {
    const checkISO = currentDate.toISO();
    if (!checkISO) break;
    
    const dashaLevels = getCurrentDashaLevels(birthISO, moonLongDeg, checkISO);
    if (!dashaLevels) {
      currentDate = currentDate.plus({ weeks: 1 });
      continue;
    }
    
    const currentPD = dashaLevels.pd?.lord || dashaLevels.ad?.lord || dashaLevels.md.lord;
    const currentSD = dashaLevels.sd?.lord || currentPD;
    
    // Detect PD change (larger events)
    if (currentPD !== lastPDPlanet && lastPDPlanet !== null) {
      const pdStart = dashaLevels.pd?.start || checkISO;
      const pdEnd = dashaLevels.pd?.end || currentDate.plus({ months: 1 }).toISO()!;
      periods.push({
        from: pdStart,
        to: pdEnd,
        level: 'PD',
        planet: currentPD
      });
    }
    
    // Detect SD change (immediate events) - OPTIMIZATION: Skip SD detection for performance
    // SD changes too frequently and creates too many periods
    // Focus on PD changes which are more significant
    // if (currentSD !== lastSDPlanet && lastSDPlanet !== null) {
    //   const sdStart = dashaLevels.sd?.start || checkISO;
    //   const sdEnd = dashaLevels.sd?.end || currentDate.plus({ days: 7 }).toISO()!;
    //   
    //   // Only add SD periods for significant planets or if different from PD
    //   if (currentSD !== currentPD) {
    //     periods.push({
    //       from: sdStart,
    //       to: sdEnd,
    //       level: 'SD',
    //       planet: currentSD
    //     });
    //   }
    // }
    
    lastPDPlanet = currentPD;
    lastSDPlanet = currentSD;
    currentDate = currentDate.plus({ weeks: 2 }); // Check bi-weekly for performance
  }
  
  // If no transitions found, create at least one period for current state
  if (periods.length === 0) {
    const nowISO = now.toISO();
    if (nowISO) {
      const currentDasha = getCurrentDashaLevels(birthISO, moonLongDeg, nowISO);
      if (currentDasha?.pd) {
        periods.push({
          from: startScan.toISO()!,
          to: DateTime.min(
            DateTime.fromISO(currentDasha.pd.end),
            endScan
          ).toISO()!,
          level: 'PD',
          planet: currentDasha.pd.lord
        });
      }
    }
  }
  
  // OPTIMIZATION: Limit to max 15 periods to keep performance reasonable
  // This prevents processing too many dasha transitions
  if (periods.length > 15) {
    return periods.slice(0, 15);
  }
  
  return periods;
}

/**
 * Consolidate consecutive or overlapping events of the same category
 * Merges events into wider time windows and highlights Rahu peak periods
 */
function consolidateEvents(events: FutureEvent[]): FutureEvent[] {
  if (events.length === 0) return [];
  
  const consolidated: FutureEvent[] = [];
  const eventsByCategory = new Map<FutureEventCategory, FutureEvent[]>();
  
  // Group by category
  for (const event of events) {
    const existing = eventsByCategory.get(event.category) || [];
    existing.push(event);
    eventsByCategory.set(event.category, existing);
  }
  
  // For each category, merge consecutive/overlapping events
  for (const [category, categoryEvents] of eventsByCategory.entries()) {
    // Sort by start time
    const sorted = categoryEvents.sort((a, b) => 
      DateTime.fromISO(a.window.from).toMillis() - DateTime.fromISO(b.window.from).toMillis()
    );
    
    let currentGroup: FutureEvent[] = [sorted[0]];
    
    for (let i = 1; i < sorted.length; i++) {
      const current = sorted[i];
      const lastInGroup = currentGroup[currentGroup.length - 1];
      
      const currentStart = DateTime.fromISO(current.window.from);
      const lastEnd = DateTime.fromISO(lastInGroup.window.to);
      
      // Merge if within 30 days of each other (consecutive/close periods)
      const daysBetween = currentStart.diff(lastEnd, 'days').days;
      
      if (daysBetween <= 30) {
        // Add to current group
        currentGroup.push(current);
      } else {
        // Finalize current group and start new one
        if (currentGroup.length > 1) {
          consolidated.push(mergeEventGroup(currentGroup));
        } else {
          consolidated.push(currentGroup[0]);
        }
        currentGroup = [current];
      }
    }
    
    // Finalize last group
    if (currentGroup.length > 1) {
      consolidated.push(mergeEventGroup(currentGroup));
    } else if (currentGroup.length === 1) {
      consolidated.push(currentGroup[0]);
    }
  }
  
  // Sort by start time
  return consolidated.sort((a, b) => 
    DateTime.fromISO(a.window.from).toMillis() - DateTime.fromISO(b.window.from).toMillis()
  );
}

/**
 * Merge a group of events into a single consolidated event
 * Identifies Rahu high-probability windows within the merged period
 */
function mergeEventGroup(events: FutureEvent[]): FutureEvent {
  if (events.length === 1) return events[0];
  
  // Find earliest start and latest end
  const starts = events.map(e => DateTime.fromISO(e.window.from));
  const ends = events.map(e => DateTime.fromISO(e.window.to));
  const earliestStart = DateTime.min(...starts);
  const latestEnd = DateTime.max(...ends);
  
  if (!earliestStart || !latestEnd || !earliestStart.isValid || !latestEnd.isValid) {
    // Fallback to first event if DateTime operations fail
    return events[0];
  }
  
  // Collect ONLY Rahu-specific sub-periods (where Rahu is AD/PD/SD, not just MD)
  // We look for evidence that mentions "Rahu AD", "Rahu PD", or "Rahu SD"
  const rahuWindowsMap = new Map<string, { from: string; to: string }>();
  for (const event of events) {
    if (event.highProbability) {
      // Check evidence for Rahu at AD/PD/SD level (not MD which is long-term theme)
      const hasRahuInSubdasha = event.evidence.some(e => 
        e.includes('Rahu AD') || e.includes('Rahu PD') || e.includes('Rahu SD')
      );
      
      if (hasRahuInSubdasha) {
        const key = `${event.window.from}|${event.window.to}`;
        rahuWindowsMap.set(key, event.window);
      }
    }
  }
  const rahuWindows = Array.from(rahuWindowsMap.values());
  
  // Calculate average confidence (weighted by event confidence)
  const totalConfidence = events.reduce((sum, e) => sum + e.confidence, 0);
  const avgConfidence = totalConfidence / events.length;
  
  // Use the highest score event as the base
  const highestScoreEvent = events.reduce((max, e) => 
    Math.abs(e.score) > Math.abs(max.score) ? e : max
  );
  
  // Combine evidence from all events (deduplicate)
  const allEvidence = new Set<string>();
  for (const event of events) {
    event.evidence.forEach(e => allEvidence.add(e));
  }
  
  // Build consolidated description
  let description = highestScoreEvent.description;
  if (rahuWindows.length > 0) {
    description = description.split(' (During')[0]; // Remove individual dasha info
    description += ` (Extended period with ${rahuWindows.length} HIGH PROBABILITY window${rahuWindows.length > 1 ? 's' : ''} during Rahu periods)`;
  }
  
  return {
    ...highestScoreEvent,
    id: `consolidated-${highestScoreEvent.category}-${earliestStart.toMillis()}`,
    window: {
      from: earliestStart.toISO()!,
      to: latestEnd.toISO()!
    },
    confidence: avgConfidence,
    description,
    evidence: Array.from(allEvidence),
    isConsolidated: true,
    consolidatedCount: events.length,
    rahuPeakWindows: rahuWindows.length > 0 ? rahuWindows : undefined,
    highProbability: rahuWindows.length > 0 // Mark as high probability if any Rahu windows
  };
}

/**
 * Main function: Compute future events
 */
export async function computeFutureEvents(params: {
  birthISO: string;
  moonLongDeg: number;
  houseScores: Record<number, number>;
  natalPlacements: DashaContext['natalPlacements'];
  combustionMap?: DashaContext['combustionMap'];
  gender?: 'male' | 'female' | 'other';
  includeMarriage?: boolean; // If true, scan 2 years for marriage events
  ascendantSign?: number; // 1-12, needed for functional classification
}): Promise<{
  opportunities: FutureEvent[];
  cautions: FutureEvent[];
}> {
  const {
    birthISO,
    moonLongDeg,
    houseScores,
    natalPlacements,
    combustionMap,
    gender,
    includeMarriage = true,
    ascendantSign
  } = params;

  // OPTIMIZATION: Check cache to avoid recomputation
  // Create a stable cache key from input parameters
  const cacheKey = JSON.stringify({
    birthISO,
    moonLongDeg: moonLongDeg.toFixed(3), // Round to avoid floating point diffs
    houseScores,
    hasNatal: !!natalPlacements,
    hasCombustion: !!combustionMap,
    gender,
    includeMarriage,
    ascendantSign
  });
  
  if (lastComputeParams === cacheKey && lastComputeResult) {
    return lastComputeResult;
  }

  const allEvents: FutureEvent[] = [];
  const now = DateTime.now();

  // Scan 6-12 months for most events
  const mainPeriods = getFutureDashaPeriods(birthISO, moonLongDeg, 12);
  
  // Additional 2-year scan for marriage events only
  const marriagePeriods = includeMarriage
    ? getFutureDashaPeriods(birthISO, moonLongDeg, 24)
    : [];

  // Process each future period
  const periodsToScan = includeMarriage ? marriagePeriods : mainPeriods;
  
  for (const period of periodsToScan) {
    const periodStart = DateTime.fromISO(period.from);
    const monthsAhead = periodStart.diff(now, 'months').months;
    
    console.log(`[futureEventPredictor] Processing ${period.level} ${period.planet}:`, 
                period.from, '→', period.to, '(', monthsAhead.toFixed(1), 'months ahead)');
    
    // Get dasha levels for this period (middle of period for stability)
    const periodMid = DateTime.fromISO(period.from).plus({ 
      milliseconds: DateTime.fromISO(period.to).diff(DateTime.fromISO(period.from)).milliseconds / 2 
    }).toISO();
    
    if (!periodMid) continue;
    
    const dashaLevels = getCurrentDashaLevels(birthISO, moonLongDeg, periodMid);
    
    if (!dashaLevels) {
      console.log('[futureEventPredictor] No dasha levels found for period');
      continue;
    }

    console.log(`[futureEventPredictor] Dasha hierarchy: MD=${dashaLevels.md.lord} AD=${dashaLevels.ad?.lord} PD=${dashaLevels.pd?.lord} SD=${dashaLevels.sd?.lord}`);

    // Helper to calculate which houses a planet rules based on ascendant sign
    const getRuledHouses = (planet: PlanetName): HouseNum[] => {
      if (!ascendantSign) return [];
      
      const SIGN_LORDS: Record<number, PlanetName> = {
        1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
        7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
      };
      
      const ruledHouses: HouseNum[] = [];
      
      // For each house (1-12), calculate which sign it corresponds to
      for (let house = 1; house <= 12; house++) {
        const signForHouse = ((ascendantSign - 1 + house - 1) % 12) + 1;
        if (SIGN_LORDS[signForHouse] === planet) {
          ruledHouses.push(house as HouseNum);
        }
      }
      
      return ruledHouses;
    };

    // Helper to calculate aspects for a planet
    const getAspects = (planet: PlanetName, house: HouseNum): HouseNum[] => {
      const seventh = ((house - 1 + 6) % 12) + 1 as HouseNum;
      const aspects: HouseNum[] = [seventh];
      
      if (planet === 'Mars') {
        aspects.push(((house - 1 + 3) % 12) + 1 as HouseNum); // 4th
        aspects.push(((house - 1 + 7) % 12) + 1 as HouseNum); // 8th
      } else if (planet === 'Jupiter') {
        aspects.push(((house - 1 + 4) % 12) + 1 as HouseNum); // 5th
        aspects.push(((house - 1 + 8) % 12) + 1 as HouseNum); // 9th
      } else if (planet === 'Saturn') {
        aspects.push(((house - 1 + 2) % 12) + 1 as HouseNum); // 3rd
        aspects.push(((house - 1 + 9) % 12) + 1 as HouseNum); // 10th
      } else if (planet === 'Rahu' || planet === 'Ketu') {
        aspects.push(((house - 1 + 4) % 12) + 1 as HouseNum); // 5th
        aspects.push(((house - 1 + 8) % 12) + 1 as HouseNum); // 9th
      }
      
      return aspects;
    };

    // Build dasha context - convert dasha levels object to array format expected by rules
    // Rules expect levels as [MD, AD, PD, SD] tuple array with rules, occupies, aspects properties
    const dashaLevelsArray: [DashaLevel, DashaLevel, DashaLevel, DashaLevel] = [
      {
        level: 'MD',
        planet: dashaLevels.md.lord as PlanetName,
        from: dashaLevels.md.start,
        to: dashaLevels.md.end,
        rules: getRuledHouses(dashaLevels.md.lord as PlanetName),
        occupies: natalPlacements?.[dashaLevels.md.lord as PlanetName]?.house || 1,
        aspects: natalPlacements?.[dashaLevels.md.lord as PlanetName]?.house 
          ? getAspects(dashaLevels.md.lord as PlanetName, natalPlacements[dashaLevels.md.lord as PlanetName]!.house)
          : []
      },
      {
        level: 'AD',
        planet: (dashaLevels.ad?.lord || dashaLevels.md.lord) as PlanetName,
        from: dashaLevels.ad?.start || dashaLevels.md.start,
        to: dashaLevels.ad?.end || dashaLevels.md.end,
        rules: getRuledHouses((dashaLevels.ad?.lord || dashaLevels.md.lord) as PlanetName),
        occupies: natalPlacements?.[(dashaLevels.ad?.lord || dashaLevels.md.lord) as PlanetName]?.house || 1,
        aspects: natalPlacements?.[(dashaLevels.ad?.lord || dashaLevels.md.lord) as PlanetName]?.house
          ? getAspects((dashaLevels.ad?.lord || dashaLevels.md.lord) as PlanetName, natalPlacements[(dashaLevels.ad?.lord || dashaLevels.md.lord) as PlanetName]!.house)
          : []
      },
      {
        level: 'PD',
        planet: (dashaLevels.pd?.lord || dashaLevels.ad?.lord || dashaLevels.md.lord) as PlanetName,
        from: dashaLevels.pd?.start || dashaLevels.ad?.start || dashaLevels.md.start,
        to: dashaLevels.pd?.end || dashaLevels.ad?.end || dashaLevels.md.end,
        rules: getRuledHouses((dashaLevels.pd?.lord || dashaLevels.ad?.lord || dashaLevels.md.lord) as PlanetName),
        occupies: natalPlacements?.[(dashaLevels.pd?.lord || dashaLevels.ad?.lord || dashaLevels.md.lord) as PlanetName]?.house || 1,
        aspects: natalPlacements?.[(dashaLevels.pd?.lord || dashaLevels.ad?.lord || dashaLevels.md.lord) as PlanetName]?.house
          ? getAspects((dashaLevels.pd?.lord || dashaLevels.ad?.lord || dashaLevels.md.lord) as PlanetName, natalPlacements[(dashaLevels.pd?.lord || dashaLevels.ad?.lord || dashaLevels.md.lord) as PlanetName]!.house)
          : []
      },
      {
        level: 'SD',
        planet: (dashaLevels.sd?.lord || dashaLevels.pd?.lord || dashaLevels.ad?.lord || dashaLevels.md.lord) as PlanetName,
        from: dashaLevels.sd?.start || dashaLevels.pd?.start || dashaLevels.ad?.start || dashaLevels.md.start,
        to: dashaLevels.sd?.end || dashaLevels.pd?.end || dashaLevels.ad?.end || dashaLevels.md.end,
        rules: getRuledHouses((dashaLevels.sd?.lord || dashaLevels.pd?.lord || dashaLevels.ad?.lord || dashaLevels.md.lord) as PlanetName),
        occupies: natalPlacements?.[(dashaLevels.sd?.lord || dashaLevels.pd?.lord || dashaLevels.ad?.lord || dashaLevels.md.lord) as PlanetName]?.house || 1,
        aspects: natalPlacements?.[(dashaLevels.sd?.lord || dashaLevels.pd?.lord || dashaLevels.ad?.lord || dashaLevels.md.lord) as PlanetName]?.house
          ? getAspects((dashaLevels.sd?.lord || dashaLevels.pd?.lord || dashaLevels.ad?.lord || dashaLevels.md.lord) as PlanetName, natalPlacements[(dashaLevels.sd?.lord || dashaLevels.pd?.lord || dashaLevels.ad?.lord || dashaLevels.md.lord) as PlanetName]!.house)
          : []
      }
    ];

    const dashaContext: DashaContext = {
      window: period,
      levels: dashaLevelsArray,
      houseScores: houseScores as any,
      birthDate: birthISO,
      gender,
      lagnaSign: ascendantSign,
      natalPlacements,
      combustionMap
    };

    // Simple transit context (can be enhanced with real ephemeris later)
    // For now, estimate transit support based on slow-moving planets
    const transitContext: TransitContext = {
      positions: [],
      planetsTransitingDashaOccupied: {} as any
    };
    
    // Calculate PD/SD emphasis multiplier
    // Events are more likely during PD/SD of relevant planets
    const pdPlanet = dashaLevels.pd?.lord;
    const sdPlanet = dashaLevels.sd?.lord;
    const adPlanet = dashaLevels.ad?.lord;
    const mdPlanet = dashaLevels.md.lord;
    
    // CRITICAL VEDIC PRINCIPLE: Rahu delivers results of the next higher level
    // Rahu PD delivers AD results, Rahu AD delivers MD results
    // This makes Rahu periods HIGH PROBABILITY for manifestation
    const isRahuPD = pdPlanet === 'Rahu';
    const isRahuSD = sdPlanet === 'Rahu';
    const isRahuAD = adPlanet === 'Rahu';
    const hasRahuInDasha = isRahuPD || isRahuSD || isRahuAD || mdPlanet === 'Rahu';
    
    // Effective planet for result delivery (accounting for Rahu's principle)
    const effectivePDPlanet = isRahuPD ? adPlanet : pdPlanet;
    const effectiveSDPlanet = isRahuSD ? pdPlanet : sdPlanet;
    const effectiveADPlanet = isRahuAD ? mdPlanet : adPlanet;
    
    const pdSdEmphasis = period.level === 'SD' ? 1.3 : period.level === 'PD' ? 1.2 : 1.0;
    
    // Additional boost for Rahu periods (high probability of manifestation)
    const rahuBoost = hasRahuInDasha ? 1.15 : 1.0;

    // Run all feed rules (combine timing and natal rules)
    const allRules = [...RULES, ...NATAL_PREDICTION_RULES];
    
    console.log('[futureEventPredictor] Testing', allRules.length, 'rules for period', period.from);
    let triggeredCount = 0;
    let passedThresholdCount = 0;
    
    for (const rule of allRules) {
      try {
        // Check if rule triggers
        if (!rule.triggers(dashaContext, transitContext)) continue;
        
        triggeredCount++;

        // Calculate score
        const result = rule.score(dashaContext, transitContext);
        
        // Apply PD/SD emphasis multiplier
        // Events manifest more strongly when the triggering planet is at PD/SD level
        let adjustedScore = result.value;
        const rulePlanets = dashaContext.levels.map(l => l.planet);
        
        // Check if effective planets (accounting for Rahu delivery) are relevant
        const isPDRelevant = effectivePDPlanet && rulePlanets.includes(effectivePDPlanet as any);
        const isSDRelevant = effectiveSDPlanet && rulePlanets.includes(effectiveSDPlanet as any);
        
        if (isSDRelevant) {
          adjustedScore *= 1.3; // SD creates immediate events
        } else if (isPDRelevant) {
          adjustedScore *= 1.2; // PD creates significant events
        }
        
        // Apply Rahu boost (high probability of manifestation)
        if (hasRahuInDasha) {
          adjustedScore *= rahuBoost;
        }
        
        // Check threshold BEFORE dusthana adjustments
        // This ensures functionally benefic placements don't eliminate warnings completely
        const baseAbsScore = Math.abs(adjustedScore);
        
        // TEMPORARY: Remove threshold for cautions to debug - show ALL cautions regardless of score
        // Opportunities need stronger signal (30+), cautions shown at any score for debugging
        const isNegativeEvent = adjustedScore < 0;
        const minThreshold = isNegativeEvent ? 0 : 30; // NO threshold for cautions (debugging)
        
        if (baseAbsScore < minThreshold) continue; // Below minimum confidence, skip
        
        // Special handling for negative events (6th/8th/12th house themes)
        // Health hazards, accidents, losses, expenses, hidden enemies, etc.
        const isDusthanaRule = rule.key.includes('accident') || 
                              rule.key.includes('health') || 
                              rule.key.includes('loss') ||
                              rule.key.includes('expense') ||
                              rule.key.includes('litigation') ||
                              rule.key.includes('hidden') ||
                              rule.areas.some(a => ['AccidentRisk', 'Health', 'Litigation'].includes(a));
        
        let dusthanaMultiplier = 1.0;
        if (isNegativeEvent && isDusthanaRule) {
          const dusthanaSeverity = calculateDusthanaSeverity(dashaLevels, natalPlacements, ascendantSign);
          dusthanaMultiplier = dusthanaSeverity.multiplier;
          adjustedScore *= dusthanaMultiplier;
          
          if (dusthanaSeverity.reasons.length > 0 && triggeredCount <= 5) {
            console.log(`[futureEventPredictor] Dusthana severity (${dusthanaMultiplier.toFixed(2)}x):`, 
                       dusthanaSeverity.reasons.join('; '));
          }
        }
        
        const absScore = Math.abs(adjustedScore);
        
        if (triggeredCount <= 5) {
          console.log('[futureEventPredictor] Rule triggered:', rule.key, 
                     'baseScore:', result.value.toFixed(1), 
                     'adjusted:', adjustedScore.toFixed(1), 
                     'absScore:', absScore.toFixed(1),
                     'PD/SD boost:', isPDRelevant || isSDRelevant ? 'YES' : 'NO',
                     'Rahu:', hasRahuInDasha ? 'YES' : 'NO',
                     'Dusthana:', dusthanaMultiplier !== 1.0 ? `${dusthanaMultiplier.toFixed(2)}x` : 'NO');
        }
        
        // Final threshold check - functional benefics reduce severity but don't eliminate alerts
        // TEMPORARY: NO final threshold for cautions - show ALL cautions for debugging
        const finalMinThreshold = isNegativeEvent ? 0 : 25;
        const baseMinThreshold = isNegativeEvent ? 0 : 40;
        
        if (absScore < finalMinThreshold && baseAbsScore < baseMinThreshold) continue;
        
        passedThresholdCount++;

        // Determine category from template, rule key, or life areas
        let category: FutureEventCategory = 'recognition_awards'; // default fallback
        
        // Try template key first
        const templateCategory = rule.templateKey ? TEMPLATE_TO_CATEGORY[rule.templateKey] : undefined;
        if (templateCategory) {
          category = templateCategory;
        }
        // Try mapping from rule key patterns
        else if (rule.key.includes('career') || rule.key.includes('promotion')) {
          category = adjustedScore > 0 ? 'career_success' : 'job_safeguard';
        }
        else if (rule.key.includes('wealth') || rule.key.includes('prosperity') || rule.key.includes('income')) {
          category = adjustedScore > 0 ? 'wealth_inflow' : 'monetary_loss';
        }
        else if (rule.key.includes('investment')) {
          category = adjustedScore > 0 ? 'investments_opportunity' : 'no_investment';
        }
        else if (rule.key.includes('property') || rule.key.includes('vehicle') || rule.key.includes('land')) {
          category = adjustedScore > 0 ? 'property_gains' : 'property_disputes';
        }
        else if (rule.key.includes('marriage') || rule.key.includes('spouse') || rule.key.includes('partnership')) {
          category = adjustedScore > 0 ? 'marriage_relationship' : 'relationship_strain';
        }
        else if (rule.key.includes('children') || rule.key.includes('child')) {
          category = adjustedScore > 0 ? 'children_blessing' : 'children_safety';
        }
        else if (rule.key.includes('health') || rule.key.includes('hygiene')) {
          category = 'health_hazards';
        }
        else if (rule.key.includes('accident')) {
          category = 'accident_risk';
        }
        else if (rule.key.includes('mother')) {
          category = 'mother_health';
        }
        else if (rule.key.includes('father')) {
          category = 'father_health';
        }
        else if (rule.key.includes('travel')) {
          category = adjustedScore > 0 ? 'travel_opportunity' : 'travel_hazards';
        }
        else if (rule.key.includes('litigation') || rule.key.includes('legal')) {
          category = adjustedScore > 0 ? 'legal_resolution' : 'litigation_risk';
        }
        else if (rule.key.includes('spiritual')) {
          category = adjustedScore > 0 ? 'spiritual_growth' : 'spiritual_crisis';
        }
        else if (rule.key.includes('loss') || rule.key.includes('expense')) {
          category = 'monetary_loss';
        }
        else if (rule.key.includes('education') || rule.key.includes('learning')) {
          category = 'education';
        }
        // Fall back to life area mapping
        else if (rule.areas[0]) {
          const lifeArea = rule.areas[0] as LifeArea;
          const categories = LIFE_AREA_TO_CATEGORIES[lifeArea];
          if (categories && categories.length > 0) {
            // Pick positive or negative category based on score
            if (adjustedScore > 0) {
              category = categories.find(c => !c.includes('hazard') && !c.includes('risk') && !c.includes('loss')) || categories[0];
            } else {
              category = categories.find(c => c.includes('hazard') || c.includes('risk') || c.includes('loss')) || categories[0];
            }
          }
        }

        const categoryInfo = getCategoryInfo(category);
        const type: 'opportunity' | 'caution' = adjustedScore > 0 ? 'opportunity' : 'caution';
        
        const severity = adjustedScore >= 50 ? 'positive' :
                        adjustedScore >= 0 ? 'mixed' :
                        adjustedScore <= -50 ? 'alert' : 'caution';

        // Generate description from rule - PRIORITY:
        // 1. rule.customPrediction (for natal yogas/special conditions)
        // 2. Dynamic templateKey from score result (for timing rules)
        // 3. Rule's templateKey (fallback)
        // 4. Category description (last resort)
        let message = '';
        let dynamicTitle: string | undefined = undefined;
        
        if (rule.customPrediction) {
          message = rule.customPrediction;
        } else {
          // Get dynamic templateKey if available (from score result)
          const dynamicTemplateKey = result.templateKey;
          const finalTemplateKey = dynamicTemplateKey || rule.templateKey;
          
          // Try to get message from template
          if (finalTemplateKey && finalTemplateKey in TEMPLATES) {
            const templateWindow = result.customWindow || period;
            const templateFn = TEMPLATES[finalTemplateKey as TemplateKey];
            message = templateFn({ window: templateWindow });
            
            // Generate dynamic title based on template key
            if (finalTemplateKey.includes('_positive')) {
              // Positive variant - use uplifting title
              if (category === 'relationship_strain' || category === 'marriage_relationship') {
                dynamicTitle = 'Partnership Harmony';
                console.log('[futureEventPredictor] Setting dynamic title for category:', category, 'templateKey:', finalTemplateKey);
              } else if (category === 'accident_risk') {
                dynamicTitle = 'Physical Wellbeing';
              } else if (category === 'health_hazards') {
                dynamicTitle = 'Health Vitality';
              } else if (category === 'litigation_risk' || category === 'legal_resolution') {
                dynamicTitle = 'Legal Clarity';
              } else if (category === 'father_health') {
                dynamicTitle = 'Father Support';
              } else if (category === 'mother_health') {
                dynamicTitle = 'Mother Wellness';
              }
            } else if (finalTemplateKey.includes('_alert')) {
              // Alert variant - keep cautionary title but may be default
              // Use default categoryInfo.label
            }
          } else {
            // Fallback to category description
            message = `${categoryInfo.description} likely during this period`;
          }
        }
        
        // Add PD/SD context to description
        let dashaInfo = '';
        if (isRahuPD) {
          dashaInfo = ` (During Rahu PD - delivers ${effectivePDPlanet} results)`;
        } else if (isRahuSD) {
          dashaInfo = ` (During Rahu SD - delivers ${effectiveSDPlanet} results)`;
        } else if (isRahuAD) {
          dashaInfo = ` (During Rahu AD - delivers ${effectiveADPlanet} results)`;
        } else {
          dashaInfo = ` (During ${pdPlanet} PD${sdPlanet && sdPlanet !== pdPlanet ? ` / ${sdPlanet} SD` : ''})`;
        }
        
        // Build evidence with Rahu delivery principle if applicable
        const evidenceNotes = [...result.notes];
        
        // Add dasha level context
        evidenceNotes.push(`Active Dasha: ${mdPlanet} MD → ${adPlanet} AD → ${pdPlanet} PD → ${sdPlanet} SD`);
        
        // Add Rahu delivery principle explanation
        if (hasRahuInDasha) {
          if (isRahuPD) evidenceNotes.push(`⚡ Rahu in PD delivers results of ${effectivePDPlanet} (AD lord) - HIGH manifestation probability`);
          if (isRahuSD) evidenceNotes.push(`⚡ Rahu in SD delivers results of ${effectiveSDPlanet} (PD lord) - HIGH manifestation probability`);
          if (isRahuAD) evidenceNotes.push(`⚡ Rahu in AD delivers results of ${effectiveADPlanet} (MD lord) - HIGH manifestation probability`);
          if (mdPlanet === 'Rahu') evidenceNotes.push(`⚡ Rahu Mahadasha - amplifies results throughout the period`);
        }
        
        // Add functional benefic/malefic context if ascendant available
        if (ascendantSign && natalPlacements) {
          const funcMap = classifyFunctionalForLagna(ascendantSign);
          const relevantPlanets = [mdPlanet, adPlanet, pdPlanet, sdPlanet].filter((p): p is PlanetName => 
            p !== 'Rahu' && p !== 'Ketu' && p !== undefined
          );
          
          for (const planet of relevantPlanets.slice(0, 2)) { // Check top 2 planets
            const funcNature = funcMap[planet as keyof typeof funcMap];
            if (funcNature === 'benefic') {
              evidenceNotes.push(`${planet} is functional benefic for ${getSignName(ascendantSign)} ascendant`);
            } else if (funcNature === 'malefic') {
              evidenceNotes.push(`${planet} is functional malefic for ${getSignName(ascendantSign)} ascendant`);
            } else if (funcNature === 'neutral') {
              evidenceNotes.push(`${planet} is neutral for ${getSignName(ascendantSign)} ascendant`);
            }
          }
        }
        
        // Add house placement context for key planets
        if (natalPlacements) {
          const keyPlanet = pdPlanet !== 'Rahu' && pdPlanet !== 'Ketu' ? pdPlanet : 
                           adPlanet !== 'Rahu' && adPlanet !== 'Ketu' ? adPlanet : mdPlanet;
          
          if (keyPlanet && keyPlanet !== 'Rahu' && keyPlanet !== 'Ketu') {
            const placement = natalPlacements[keyPlanet as PlanetName];
            if (placement?.house) {
              evidenceNotes.push(`${keyPlanet} placed in ${getHouseName(placement.house)} in birth chart`);
            }
          }
        }

        const event: FutureEvent = {
          id: `${rule.key}-${period.from}`,
          category,
          type,
          severity,
          score: adjustedScore,
          title: dynamicTitle || categoryInfo.label,
          description: message + dashaInfo,
          window: result.customWindow || period,
          confidence: absScore,
          lifeAreas: rule.areas,
          remedies: rule.remedies,
          evidence: evidenceNotes,
          highProbability: hasRahuInDasha
        };
        
        console.log('[futureEventPredictor] Created event:', { 
          ruleKey: rule.key, 
          category, 
          dynamicTitle, 
          finalTitle: event.title 
        });

        allEvents.push(event);
      } catch (err) {
        console.error(`[futureEventPredictor] Error processing rule ${rule.key}:`, err);
      }
    }
  }

  // Sort by window start time, then by confidence
  const sortedEvents = allEvents.sort((a, b) => {
    const timeCompare = DateTime.fromISO(a.window.from).toMillis() - DateTime.fromISO(b.window.from).toMillis();
    if (timeCompare !== 0) return timeCompare;
    return b.confidence - a.confidence;
  });

  // Consolidate consecutive/overlapping events of the same category
  const consolidatedEvents = consolidateEvents(sortedEvents);
  
  // Separate opportunities and cautions
  const opportunities = consolidatedEvents.filter(e => e.type === 'opportunity');
  const cautions = consolidatedEvents.filter(e => e.type === 'caution');

  // Cache the result
  const result = { opportunities, cautions };
  lastComputeParams = cacheKey;
  lastComputeResult = result;

  return result;
}

/**
 * Group events by category for display
 */
export function groupEventsByCategory(events: FutureEvent[]): Map<FutureEventCategory, FutureEvent[]> {
  const grouped = new Map<FutureEventCategory, FutureEvent[]>();
  
  for (const event of events) {
    const existing = grouped.get(event.category) || [];
    existing.push(event);
    grouped.set(event.category, existing);
  }
  
  return grouped;
}

/**
 * Get top N events by confidence
 */
export function getTopEvents(events: FutureEvent[], limit: number = 10): FutureEvent[] {
  return events.slice(0, limit);
}

/**
 * Check if a planet is functional malefic or occupies a dusthana (6/8/12)
 * Used for health hazard, accident, and negative event predictions
 */
function isDusthanaInfluence(
  planet: PlanetName,
  natalPlacements?: DashaContext['natalPlacements']
): { isDusthana: boolean; houses: number[]; severity: 'high' | 'medium' | 'low' } {
  if (!natalPlacements || !natalPlacements[planet]) {
    return { isDusthana: false, houses: [], severity: 'low' };
  }
  
  const placement = natalPlacements[planet];
  const occupiedHouse = placement?.house;
  const ruledHouses = placement?.rules || [];
  
  const dusthanaHouses = [6, 8, 12];
  const afflictedHouses: number[] = [];
  
  // Check if planet occupies 6/8/12
  if (occupiedHouse && dusthanaHouses.includes(occupiedHouse)) {
    afflictedHouses.push(occupiedHouse);
  }
  
  // Check if planet rules 6/8/12 (and that house is empty or afflicted)
  ruledHouses.forEach(h => {
    if (dusthanaHouses.includes(h)) {
      afflictedHouses.push(h);
    }
  });
  
  // Determine severity
  let severity: 'high' | 'medium' | 'low' = 'low';
  if (afflictedHouses.includes(6) || afflictedHouses.includes(8) || afflictedHouses.includes(12)) {
    severity = 'medium';
  }
  if (afflictedHouses.length >= 2) {
    severity = 'high';
  }
  
  return {
    isDusthana: afflictedHouses.length > 0,
    houses: afflictedHouses,
    severity
  };
}

/**
 * Check if opposite house is empty (worsens dusthana results)
 * 6th opposite = 12th, 8th opposite = 2nd, 12th opposite = 6th
 */
function isOppositeHouseEmpty(
  house: number,
  natalPlacements?: DashaContext['natalPlacements']
): boolean {
  if (!natalPlacements) return false;
  
  const oppositeHouse = ((house + 5) % 12) + 1; // 6 houses away
  
  // Check if any planet occupies the opposite house
  const hasOccupant = Object.values(natalPlacements).some(
    p => p?.house === oppositeHouse
  );
  
  return !hasOccupant;
}

/**
 * Calculate dusthana event severity multiplier
 * For 6th/8th/12th house events (health hazards, accidents, losses, expenses)
 * CRITICAL: Functional benefics in dusthana provide PROTECTION
 *           Functional malefics in dusthana AMPLIFY negative results
 */
function calculateDusthanaSeverity(
  dashaLevels: any,
  natalPlacements?: DashaContext['natalPlacements'],
  ascendantSign?: number
): { multiplier: number; reasons: string[] } {
  const reasons: string[] = [];
  let multiplier = 1.0;
  
  const pdPlanet = dashaLevels.pd?.lord as PlanetName;
  const sdPlanet = dashaLevels.sd?.lord as PlanetName;
  const adPlanet = dashaLevels.ad?.lord as PlanetName;
  
  // Helper to get functional nature
  const getFunctionalNature = (planet: PlanetName): 'benefic' | 'malefic' | 'neutral' | 'unknown' => {
    if (!ascendantSign) {
      return 'unknown'; // Can't determine without ascendant
    }
    
    // Use existing functional classification from astroEngine
    const functionalMap = classifyFunctionalForLagna(ascendantSign) as Record<PlanetName, 'benefic' | 'malefic' | 'neutral'>;
    return functionalMap[planet] || 'neutral';
  };
  
  // Check PD planet
  if (pdPlanet) {
    const pdDusthana = isDusthanaInfluence(pdPlanet, natalPlacements);
    if (pdDusthana.isDusthana) {
      const functionalNature = getFunctionalNature(pdPlanet);
      
      if (functionalNature === 'benefic') {
        // PROTECTION: Functional benefic in dusthana reduces negative results
        multiplier *= 0.7; // 30% reduction in negative impact
        reasons.push(`${pdPlanet} PD (FUNCTIONAL BENEFIC) in houses ${pdDusthana.houses.join(', ')} - PROVIDES PROTECTION from negative results`);
      } else if (functionalNature === 'malefic') {
        // AMPLIFICATION: Functional malefic in dusthana increases negative results
        const amplification = pdDusthana.severity === 'high' ? 1.5 : pdDusthana.severity === 'medium' ? 1.4 : 1.3;
        multiplier *= amplification;
        reasons.push(`${pdPlanet} PD (FUNCTIONAL MALEFIC) in houses ${pdDusthana.houses.join(', ')} - AMPLIFIES negative results (${pdDusthana.severity} severity)`);
      } else {
        // Neutral or unknown - moderate impact
        multiplier *= pdDusthana.severity === 'high' ? 1.3 : pdDusthana.severity === 'medium' ? 1.2 : 1.1;
        reasons.push(`${pdPlanet} PD activates houses ${pdDusthana.houses.join(', ')} (${pdDusthana.severity} severity)`);
      }
      
      // Check if opposite house is empty (only worsens if functional malefic or neutral)
      if (functionalNature !== 'benefic') {
        pdDusthana.houses.forEach(h => {
          if (isOppositeHouseEmpty(h, natalPlacements)) {
            multiplier *= 1.15;
            reasons.push(`House ${h} opposite (${(h + 5) % 12 + 1}) is empty - worsens results`);
          }
        });
      }
    }
  }
  
  // Check SD planet
  if (sdPlanet && sdPlanet !== pdPlanet) {
    const sdDusthana = isDusthanaInfluence(sdPlanet, natalPlacements);
    if (sdDusthana.isDusthana) {
      const functionalNature = getFunctionalNature(sdPlanet);
      
      if (functionalNature === 'benefic') {
        multiplier *= 0.8; // Less protection than PD, but still helps
        reasons.push(`${sdPlanet} SD (FUNCTIONAL BENEFIC) provides some protection`);
      } else if (functionalNature === 'malefic') {
        multiplier *= sdDusthana.severity === 'high' ? 1.4 : sdDusthana.severity === 'medium' ? 1.3 : 1.2;
        reasons.push(`${sdPlanet} SD (FUNCTIONAL MALEFIC) activates houses ${sdDusthana.houses.join(', ')}`);
      } else {
        multiplier *= 1.15;
        reasons.push(`${sdPlanet} SD activates houses ${sdDusthana.houses.join(', ')}`);
      }
    }
  }
  
  // Rahu involvement makes it HIGHLY PROBABLE (unless protected by functional benefic)
  const hasRahu = [pdPlanet, sdPlanet, adPlanet, dashaLevels.md.lord].includes('Rahu' as any);
  if (hasRahu) {
    const pdNature = pdPlanet ? getFunctionalNature(pdPlanet) : 'unknown';
    if (pdNature === 'benefic') {
      multiplier *= 1.1; // Reduced Rahu effect if protected by benefic PD
      reasons.push('Rahu involvement but functional benefic PD provides some protection');
    } else {
      multiplier *= 1.25;
      reasons.push('Rahu involvement - HIGH PROBABILITY of manifestation');
    }
  }
  
  return { multiplier, reasons };
}
